import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, ChevronDown, Menu, X, Languages, Loader2 } from 'lucide-react';
import { useSiteData } from '../lib/SiteDataContext';
import { useLang } from '../lib/LanguageContext';
import { resolveImage } from '../lib/api';

const NavItem = ({ to, label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <li
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink
        to={to}
        className={({ isActive }) =>
          `inline-flex items-center gap-1 px-3 py-4 text-[14px] font-medium tracking-wide transition-colors duration-200 ${
            isActive ? 'text-[#C9A86A]' : 'text-white hover:text-[#C9A86A]'
          }`
        }
        end
      >
        <span>{label}</span>
        {children && children.length > 0 && <ChevronDown size={14} className="opacity-80" strokeWidth={2} />}
      </NavLink>
      {children && children.length > 0 && open && (
        <div className="absolute left-0 top-full z-40 min-w-[280px] border-t-2 border-[#C9A86A] bg-white shadow-2xl">
          <ul className="py-2">
            {children.map((c) => (
              <li key={c.to}>
                <Link
                  to={c.to}
                  className="block px-5 py-2.5 text-[13.5px] text-[#0B2545] hover:bg-[#F6F2EA] hover:text-[#0B2545]"
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

const Header = () => {
  const { settings, ministries, investment, news } = useSiteData();
  const { lang, setLang, t, translating } = useLang();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const govLinks = (settings?.government_links || [])
    .slice().sort((a, b) => a.order - b.order);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    const found = (news || []).find((n) =>
      n.title.toLowerCase().includes(query.toLowerCase())
    );
    if (found) navigate(`/news/${found.id}`);
    else navigate('/news');
    setSearchOpen(false);
    setQuery('');
  };

  const siteName = settings?.site_name || 'South East State of Somalia';
  const siteSub = settings?.site_subtitle || 'STATE HOUSE';
  const logo = settings?.logo_url ? resolveImage(settings.logo_url) : '';

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="bg-white">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-2 px-3 py-2.5 sm:px-6 sm:py-3">
          <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3">
            {logo ? (
              <img
                src={logo}
                alt="South East State of Somalia seal"
                className="h-10 w-auto shrink-0 sm:h-12 md:h-14"
              />
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0B2545] font-serif text-lg font-bold text-[#C9A86A] sm:h-12 sm:w-12 md:h-14 md:w-14">
                {(siteName || 'S').charAt(0)}
              </div>
            )}
            <div className="min-w-0 leading-tight">
              <div className="truncate font-serif text-[14px] font-bold text-[#0B2545] sm:text-[17px] md:text-[20px]">
                {siteName}
              </div>
              <div className="hidden text-[10px] font-semibold tracking-[0.16em] text-[#7a6b3f] sm:block sm:text-[11px] sm:tracking-[0.18em]">
                {siteSub}
              </div>
            </div>
          </Link>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => setLang(lang === 'en' ? 'so' : 'en')}
              aria-label="Switch language"
              data-testid="lang-switch-btn"
              title={lang === 'en' ? 'Switch to Somali (Af-Soomaali)' : 'Switch to English'}
              className="inline-flex h-9 items-center gap-1 rounded-full border border-[#E6DDC8] px-2 text-[11.5px] font-semibold text-[#0B2545] hover:bg-[#F6F2EA] sm:h-10 sm:gap-1.5 sm:px-3 sm:text-[12.5px]"
            >
              {translating ? (
                <Loader2 size={14} className="animate-spin text-[#C9A86A]" />
              ) : (
                <Languages size={14} className="text-[#C9A86A]" />
              )}
              {/* Compact single-code on mobile, full EN/SO toggle from sm+ */}
              <span className="sm:hidden">{lang === 'en' ? 'EN' : 'SO'}</span>
              <span className="hidden sm:inline">
                <span className={lang === 'en' ? 'text-[#0B2545]' : 'text-[#0B2545]/45'}>EN</span>
                <span className="mx-1 text-[#0B2545]/30">/</span>
                <span className={lang === 'so' ? 'text-[#0B2545]' : 'text-[#0B2545]/45'}>SO</span>
              </span>
            </button>
            <button
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="Search"
              data-testid="header-search-btn"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E6DDC8] text-[#0B2545] hover:bg-[#F6F2EA] sm:h-10 sm:w-10"
            >
              <Search size={17} />
            </button>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
              data-testid="mobile-menu-btn"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E6DDC8] text-[#0B2545] hover:bg-[#F6F2EA] sm:h-10 sm:w-10 lg:hidden"
            >
              {mobileOpen ? <X size={17} /> : <Menu size={17} />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-[#E6DDC8] bg-[#F6F2EA]">
            <form onSubmit={handleSearch} className="mx-auto flex max-w-[1280px] items-center gap-3 px-3 py-3 sm:px-6">
              <Search size={18} className="text-[#0B2545]" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('Search news, ministries, documents...')}
                className="flex-1 bg-transparent text-sm text-[#0B2545] outline-none placeholder:text-[#0B2545]/50"
              />
              <button type="submit" className="rounded bg-[#0B2545] px-4 py-1.5 text-xs font-medium text-white hover:bg-[#08203a]">
                {t('Search')}
              </button>
            </form>
          </div>
        )}
      </div>

      <div className="border-b-2 border-[#C9A86A] bg-[#0B2545]">
        <div className="mx-auto max-w-[1280px] px-3 sm:px-6">
          <nav className="hidden lg:block">
            <ul className="flex items-center justify-center">
              <NavItem to="/" label={t('Home')} />
              <NavItem
                to={govLinks[0] ? `/government/${govLinks[0].slug}` : '/'}
                label={t('Government')}
                children={govLinks.map((g) => ({ to: `/government/${g.slug}`, label: g.name }))}
              />
              <NavItem
                to="/ministries"
                label={t('Ministries')}
                children={(ministries || []).slice(0, 8).map((m) => ({ to: `/ministries/${m.slug}`, label: m.name }))}
              />
              <NavItem
                to="/documents"
                label={t('Documents')}
                children={[
                  { to: '/documents/policies', label: t('Policies & Strategies') },
                  { to: '/documents/reports', label: t('Reports & Publications') },
                  { to: '/documents/laws', label: t('Laws & Regulations') }
                ]}
              />
              <NavItem
                to="/news"
                label={t('News')}
                children={[
                  { to: '/news', label: t('All News') },
                  { to: '/news?cat=presidential', label: t('Presidential') },
                  { to: '/news?cat=government', label: t('Government') }
                ]}
              />
              <NavItem to="/events" label={t('Events')} />
              <NavItem
                to="/investment/opportunities"
                label={t('Investment')}
                children={(investment || []).map((s) => ({ to: `/investment/${s.slug}`, label: s.name }))}
              />
              <NavItem to="/about" label={t('About')} />
              <NavItem to="/contact" label={t('Contact')} />
            </ul>
          </nav>

          {mobileOpen && (
            <div className="lg:hidden">
              <ul className="flex flex-col py-2">
                {[
                  { to: '/', label: t('Home') },
                  ...govLinks.slice(0, 1).map((g) => ({ to: `/government/${g.slug}`, label: t('Government') })),
                  { to: '/ministries', label: t('Ministries') },
                  { to: '/documents', label: t('Documents') },
                  { to: '/news', label: t('News') },
                  { to: '/events', label: t('Events') },
                  { to: '/investment/opportunities', label: t('Investment') },
                  { to: '/about', label: t('About') },
                  { to: '/contact', label: t('Contact') }
                ].map((i) => (
                  <li key={i.to + i.label}>
                    <Link
                      to={i.to}
                      onClick={() => setMobileOpen(false)}
                      className="block px-3 py-3 text-sm text-white hover:text-[#C9A86A]"
                    >
                      {i.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
